import {
	IChatMessage,
	IRawMediaMessageOut,
	IRawMessageOut,
	IRawVoiceMessageOut,
	SenderSource,
	MessageType,
	IChatTextMessage,
	IChatMediaMessage,
	IChatVoiceMessage,
	IChatEventMessage,
	IRawText,
} from 'src/types';
import { v4 as uuidV4 } from 'uuid';
import {
	isRawCommandMessage,
	isRawEventMessage,
	isRawMediaMessage,
	isRawTextMessage,
	isRawVoiceMessage,
} from '../models/utils/checkRawMessage';
import { IMetaResource, Resource } from '../models/Resource';
import { getNormalizedIsoDate } from '../utils';
import { IChatCommandMessage } from '../types/chat/command/IChatCommandMessage';

/**
 * Synchronous adapter that converts non-media raw WebSocket messages to ChatMessage objects.
 *
 * The naming IN is used because messages are incoming from the WebSocket server.
 * The naming RawMessageOut describes the WebSocket message format that is outgoing from the server.
 *
 * This adapter handles TEXT, COMMAND, and EVENT messages only.
 * For MEDIA and VOICE messages, use asyncRawMediaMessageAdapter_IN for proper Resource resolution.
 *
 * @template R - The type of RawMessageOut to convert (excludes MEDIA and VOICE)
 * @param raw - RawMessageOut incoming from WebSocket (TEXT, COMMAND, or EVENT)
 * @returns A ChatMessage object with the appropriate type mapping
 */
export const syncRawMessageAdapter_IN = <
	R extends Exclude<IRawMessageOut, IRawMediaMessageOut | IRawVoiceMessageOut>,
>(
	raw: R,
): IChatMessage<
	R extends IRawText
		? IChatTextMessage
		: R extends { command: unknown }
			? IChatCommandMessage
			: R extends { event: unknown }
				? IChatEventMessage
				: never
> => {
	if (isRawTextMessage(raw)) {
		const { text, ..._ro } = formatRaw(raw);

		const cm: IChatMessage<IChatTextMessage> = {
			_type: MessageType.TEXT,
			_data: text,
			..._ro,
		};
		return cm as any;
	} else if (isRawCommandMessage(raw)) {
		const message = formatRaw(raw);
		const { command, parameters, ..._ro } = message;

		const cm: IChatMessage<IChatCommandMessage> = {
			_type: MessageType.COMMAND,
			_data: { name: command, parameters } as any,
			..._ro,
		};
		return cm as any;
	} else if (isRawEventMessage(raw)) {
		const message = formatRaw(raw);
		const { event, parameters, ..._ro } = message;

		const cm: IChatMessage<IChatEventMessage> = {
			_type: MessageType.EVENT,
			_data: { name: event, parameters } as any,
			..._ro,
		};
		return cm as any;
	} else {
		throw new Error('Invalid message format: TEXT, COMMAND, or EVENT expected');
	}
};

/**
 * Asynchronous adapter that converts raw WebSocket messages with media or voice content to ChatMessage objects.
 *
 * This adapter is specialized for MEDIA and VOICE messages, resolving Resources asynchronously.
 * It fetches and transforms media metadata, making it suitable for messages that contain media attachments.
 *
 * @template R - The type of RawMessageOut to convert (MEDIA or VOICE messages)
 * @param raw - RawMessageOut incoming from WebSocket containing media or voice data
 * @returns A Promise resolving to a ChatMessage object with Resources fully resolved to IMetaResource
 */
export const asyncRawMediaMessageAdapter_IN = async <
	R extends IRawMessageOut<IRawMediaMessageOut | IRawVoiceMessageOut>,
>(
	raw: R,
): Promise<
	IChatMessage<
		R extends IRawMessageOut<IRawMediaMessageOut>
			? IChatMediaMessage<IMetaResource>
			: R extends IRawMessageOut<IRawVoiceMessageOut>
				? IChatVoiceMessage<IMetaResource>
				: never
	>
> => {
	if (isRawMediaMessage(raw)) {
		const { media, ..._ro } = formatRaw(raw);
		const _data = await Resource(media);

		const cm: IChatMessage<IChatMediaMessage<IMetaResource>> = {
			_type: MessageType.MEDIA,
			_data,
			..._ro,
		};
		return cm as any;
	} else if (isRawVoiceMessage(raw)) {
		const { voice, ..._ro } = formatRaw(raw);
		const _data = await Resource(voice);

		const cm: IChatMessage<IChatVoiceMessage<IMetaResource>> = {
			_type: MessageType.VOICE,
			_data,
			..._ro,
		};
		return cm as any;
	} else {
		throw new Error('Invalid message format: MEDIA or VOICE expected');
	}
};

const formatRaw = <T extends IRawMessageOut>(raw: T) => {
	const {
		timestamp: _timestamp = undefined,
		sender = SenderSource.USER,
		client_id,
		..._raw
	} = raw;

	const { iso: timestamp } = getNormalizedIsoDate(_timestamp);

	return {
		client_id: client_id ?? timestamp ?? uuidV4(),
		sender,
		timestamp,
		_timestamp,
		..._raw,
	};
};
